const MAX_DIST = 16 // tiles beyond which sounds are silent

export class ClientAudio {
  constructor(world) {
    this.world = world
    this._decoded = new Map()
    this._unlocked = false

    this._ctx = new AudioContext()
    this._master = this._ctx.createGain()
    this._master.connect(this._ctx.destination)

    // Re-lock when context suspends (e.g. headphones unplug, tab switch)
    this._ctx.addEventListener('statechange', () => {
      if (this._ctx.state === 'suspended') {
        this._unlocked = false
        this.ready = this._setupUnlock()
      }
    })

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this._ctx.suspend()
      else if (this._unlocked) this._ctx.resume()
    })

    // Switch iOS audio session to 'playback' category so the silent switch
    // doesn't mute us. Safari 17+ supports this directly.
    if (navigator.audioSession) {
      navigator.audioSession.type = 'playback'
    }

    this.ready = this._setupUnlock()
  }

  // iOS requires a touchstart AND touchend in quick succession — so we listen
  // for touchend/mouseup. resume() returns a Promise; we wait for it so Chrome
  // unlocks on the first gesture. The state check guards against iOS resolving
  // the promise before the context is truly running (will retry on next gesture).
  _setupUnlock() {
    return new Promise(resolve => {
      if (this._unlocked) return resolve()
      const onGesture = () => {
        this._ctx.resume().then(() => {
          if (this._ctx.state !== 'running') return
          this._unlocked = true
          document.removeEventListener('touchend', onGesture)
          document.removeEventListener('mouseup', onGesture)
          // Fallback for older iOS: play a silent-ish audio element to flip the
          // audio session to 'playback' category (bypasses the silent switch).
          // Don't connect to Web Audio graph to avoid triggering Now Playing UI.
          if (!navigator.audioSession) {
            const audio = new Audio('/tiny.mp4')
            audio.volume = 0.001
            audio.play().catch(() => {})
          }
          resolve()
        })
      }
      document.addEventListener('touchend', onGesture)
      document.addEventListener('mouseup', onGesture)
    })
  }

  toggleMute() {
    this._master.gain.value = this._master.gain.value > 0 ? 0 : 1
  }

  _load(name) {
    const promise = fetch(`/assets/audio/${name}.mp3`)
      .then(r => r.arrayBuffer())
      .then(buf => this._ctx.decodeAudioData(buf))
      .then(decoded => {
        this._decoded.set(name, decoded)
        return decoded
      })
      .catch(err => {
        this._decoded.delete(name)
        console.warn(`[audio] failed to load ${name}`, err)
        return null
      })
    this._decoded.set(name, promise)
    return promise
  }

  async play(sound, x, y) {
    if (!this._unlocked) {
      const then = performance.now()
      await this.ready
      const now = performance.now()
      const elapsed = now - then
      if (elapsed > 1000) return // dont play old as sounds
    }

    const name = sound.name
    const pitch = sound.pitch ?? 0
    const vol = sound.volume ?? 1

    if (!this._decoded.has(name)) this._load(name)
    let buffer = this._decoded.get(name)
    if (buffer instanceof Promise) buffer = await buffer
    if (!buffer) return

    const lx = this.world.camera.position.x
    const ly = this.world.camera.position.y
    const dx = x - lx
    const dy = y - ly
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist >= MAX_DIST) return

    const volume = (1 - dist / MAX_DIST) * vol
    const pan = Math.max(-1, Math.min(1, dx / (MAX_DIST * 0.5)))

    const source = this._ctx.createBufferSource()
    source.buffer = buffer
    if (pitch) source.playbackRate.value = 1 + (Math.random() * 2 - 1) * pitch

    const gain = this._ctx.createGain()
    gain.gain.value = volume

    const panner = this._ctx.createStereoPanner()
    panner.pan.value = pan
    source.connect(panner)
    panner.connect(gain)
    gain.connect(this._master)
    source.onended = () => {
      source.disconnect()
      panner.disconnect()
      gain.disconnect()
    }
    source.start()
  }
}
